"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { KleinesModal, useEingehaengt } from "../../modal/kleines-modal";
import { Trefferfeld, type Treffer } from "../../formular";

/*
 * Dialoge des Bearbeiten-Pfads (Paket E-W2): Gitterwahl für die Tabelle (schwebende Karte am Cursor oder am Menü, G-W4 `w4-popups`, Abweichung AW-064 (2))
 * und Seitenwahl für den Wiki-Verweis (Kleines Modal M mit Trefferfeld, D6a). Die Bildablage ist kein Dialog dieses Pakets: sie
 * gehört der Seite (G-W6, Paket P-W4a); die Seite fängt die Anfrage `bild` selbst ab.
 *
 * Die Dialoge fügen nichts selbst ein: sie rufen die Befehle der Hülle (`HornEinfuegeBefehle` aus `useHornEinfuegen`), die Eingaben
 * prüfen und einfügen. Ein abgelehntes Einfügen bleibt im Dialog mit einem Satz (r105, ohne Code). Es wird nichts gemessen (r260).
 */

export type EditorAnfrageArt = "tabelle" | "bild" | "seite";

/** Viewport-Koordinaten des Ankers (gleiche Form wie `HornAnker` in `@horn/editor`). */
export interface EditorAnker {
  left: number;
  top: number;
  bottom: number;
}

export interface EditorAnfrage {
  art: EditorAnfrageArt;
  anker: EditorAnker | null;
}

/** Befehle, die die Dialoge brauchen (strukturell gleich `HornEinfuegeBefehle`). */
export interface EditorDialogBefehle {
  tabelle: (zeilen: number, spalten: number) => { ok: true } | { ok: false; grund: string };
  seiteVerlinken: (daten: { href: string; titel: string }) => { ok: true } | { ok: false; grund: string };
}

/** Treffer der Seitensuche (gleiche Form wie `InternalLinkHit` in `@horn/editor`). */
export interface SeitenTreffer {
  id: string;
  label: string;
  subtitle?: string;
  href: string;
}

export const SEITENWAHL_SAETZE = {
  ungueltig: "Auf diese Seite kann nicht verwiesen werden. Bitte eine andere Seite wählen.",
  suche: "Die Suche ist gerade nicht möglich. Bitte erneut versuchen.",
} as const;

// ---------------------------------------------------------------------------------------------
// Gitterwahl

const GITTER_MAX = 8;
const GITTER_KARTE = "fixed top-0 start-0 z-70 w-fit bg-popover border border-popover-line rounded-xl shadow-md p-3";
const GITTER_ZELLE_AN = "size-6 rounded-sm border border-primary-200 bg-primary-100";
const GITTER_ZELLE_AUS = "size-6 rounded-sm border border-layer-line bg-layer";
const GITTER_FLAECHE = "grid grid-cols-8 gap-1 p-1 outline-hidden rounded-lg focus-visible:ring-2 focus-visible:ring-primary-focus";
const ABSTAND = 8;

export function EditorGitterWahl({
  anker,
  beiWahl,
  beiSchliessen,
}: {
  /** Verankerung (Cursor oder Menü-Auslöser); ohne Anker steht die Karte oben links im Fenster. */
  anker: EditorAnker | null;
  beiWahl: (zeilen: number, spalten: number) => void;
  beiSchliessen: () => void;
}) {
  const eingehaengt = useEingehaengt();
  const karte = useRef<HTMLDivElement>(null);
  const gitter = useRef<HTMLDivElement>(null);
  const [zeile, setZeile] = useState(3);
  const [spalte, setSpalte] = useState(3);

  // Platzieren vor dem ersten Zeichnen: unter dem Anker, bei zu wenig Platz darüber, nie über den Rand hinaus. Die Koordinaten
  // setzt der Baustein zur Laufzeit am Element (wie `dropdown-menue.tsx`); sie sind keine Gestaltung.
  useLayoutEffect(() => {
    const k = karte.current;
    if (!k) return;
    const b = k.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const a = anker ?? { left: ABSTAND, top: ABSTAND, bottom: ABSTAND };
    let oben = a.bottom + ABSTAND;
    if (oben + b.height > vh - ABSTAND && a.top - ABSTAND - b.height > ABSTAND) oben = a.top - ABSTAND - b.height;
    const links = Math.max(ABSTAND, Math.min(a.left, vw - b.width - ABSTAND));
    k.style.left = `${Math.round(links)}px`;
    k.style.top = `${Math.round(Math.max(ABSTAND, oben))}px`;
  }, [anker, eingehaengt]);

  useEffect(() => {
    if (!eingehaengt) return;
    gitter.current?.focus();
    const beiKlick = (e: PointerEvent) => {
      if (!karte.current?.contains(e.target as Node)) beiSchliessen();
    };
    const beiTaste = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        beiSchliessen();
      }
    };
    document.addEventListener("pointerdown", beiKlick);
    document.addEventListener("keydown", beiTaste);
    return () => {
      document.removeEventListener("pointerdown", beiKlick);
      document.removeEventListener("keydown", beiTaste);
    };
  }, [eingehaengt, beiSchliessen]);

  const taste = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "ArrowRight") setSpalte((s) => Math.min(GITTER_MAX, s + 1));
      else if (e.key === "ArrowLeft") setSpalte((s) => Math.max(1, s - 1));
      else if (e.key === "ArrowDown") setZeile((z) => Math.min(GITTER_MAX, z + 1));
      else if (e.key === "ArrowUp") setZeile((z) => Math.max(1, z - 1));
      else if (e.key === "Enter") beiWahl(zeile, spalte);
      else return;
      e.preventDefault();
    },
    [beiWahl, zeile, spalte],
  );

  if (!eingehaengt) return null;
  const zellen = [];
  for (let z = 1; z <= GITTER_MAX; z += 1) {
    for (let s = 1; s <= GITTER_MAX; s += 1) {
      zellen.push(
        <div
          key={`${z}-${s}`}
          data-zelle={`${z}-${s}`}
          className={z <= zeile && s <= spalte ? GITTER_ZELLE_AN : GITTER_ZELLE_AUS}
          onPointerEnter={() => {
            setZeile(z);
            setSpalte(s);
          }}
          onClick={() => beiWahl(z, s)}
        />,
      );
    }
  }
  return createPortal(
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-profile-popovers/contact-profile-popover-card */}
      <div ref={karte} className={GITTER_KARTE} role="dialog" aria-label="Tabelle einfügen">
        <div
          ref={gitter}
          tabIndex={0}
          role="group"
          aria-label="Größe der Tabelle wählen, mit den Pfeiltasten ändern, mit Enter einfügen"
          className={GITTER_FLAECHE}
          onKeyDown={taste}
        >
          {zellen}
        </div>
        <p className="mt-2 text-center text-sm text-muted-foreground-1" aria-live="polite">
          {spalte} {spalte === 1 ? "Spalte" : "Spalten"} × {zeile} {zeile === 1 ? "Zeile" : "Zeilen"}
        </p>
      </div>
    </>,
    document.body,
  );
}

// ---------------------------------------------------------------------------------------------
// Seitenwahl

export function EditorSeitenWahl({
  offen,
  suche,
  beiVerlinken,
  beiSchliessen,
}: {
  offen: boolean;
  /**
   * Seitensuche: bestehende, gesicherte Action der Seite (Wiki: `searchWikiPagesAction`). Der Baustein ruft sie nur auf und zeigt, was
   * zurückkommt (still gefiltert, r110); er prüft keine Rechte. Scheitert die Suche, zeigt das Trefferfeld den Fehlschlag-Satz (r105).
   */
  suche: (begriff: string) => Promise<SeitenTreffer[]>;
  beiVerlinken: (daten: { href: string; titel: string }) => { ok: true } | { ok: false; grund: string };
  beiSchliessen: () => void;
}) {
  const [gewaehlt, setGewaehlt] = useState<Treffer | null>(null);
  const bekannt = useRef(new Map<string, SeitenTreffer>());

  // Beim erneuten Öffnen ohne alte Wahl beginnen.
  useEffect(() => {
    if (offen) setGewaehlt(null);
  }, [offen]);

  return (
    <KleinesModal
      offen={offen}
      groesse="m"
      titel="Seite verlinken"
      beschreibung="Der Verweis gilt für den markierten Text; ohne Markierung wird der Seitentitel eingefügt."
      beimSchliessen={beiSchliessen}
      bestaetigenText="Verlinken"
      bestaetigenGesperrt={gewaehlt === null}
      onBestaetigen={() => {
        const treffer = gewaehlt ? bekannt.current.get(gewaehlt.id) : undefined;
        if (!treffer) return { ok: false, meldung: SEITENWAHL_SAETZE.ungueltig };
        const ergebnis = beiVerlinken({ href: treffer.href, titel: treffer.label });
        return ergebnis.ok ? { ok: true } : { ok: false, meldung: SEITENWAHL_SAETZE.ungueltig };
      }}
    >
      <Trefferfeld
        label="Seite"
        name="seite"
        platzhalter="Seitentitel eingeben …"
        startText="Tippe einen Seitentitel."
        keineTreffer="Keine Seite gefunden."
        fehlschlagText={SEITENWAHL_SAETZE.suche}
        suche={async (begriff) => {
          const treffer = await suche(begriff);
          bekannt.current = new Map(treffer.map((t) => [t.id, t]));
          return treffer.map((t) => ({ id: t.id, label: t.label, beschreibung: t.subtitle }));
        }}
        gewaehlt={gewaehlt}
        beiWahl={setGewaehlt}
      />
    </KleinesModal>
  );
}

// ---------------------------------------------------------------------------------------------
// Verteiler

/**
 * Zeigt den Dialog zur Anfrage der Hülle. Die Anfrage `bild` bleibt der Seite (Bildablage, G-W6): hier erscheint nichts.
 * Typischer Einbau: `const [anfrage, setAnfrage] = useState<EditorAnfrage | null>(null)`, derselbe Setzer geht als `beiAnfrage` an
 * den Editor und an `useHornEinfuegen`; die Seite fängt `bild` in dieser Funktion ab, alles andere geht in den Zustand.
 */
export function EditorDialoge({
  anfrage,
  befehle,
  sucheSeiten,
  beiSchliessen,
}: {
  anfrage: EditorAnfrage | null;
  befehle: EditorDialogBefehle | null;
  sucheSeiten?: (begriff: string) => Promise<SeitenTreffer[]>;
  beiSchliessen: () => void;
}) {
  if (!anfrage || !befehle) return null;
  if (anfrage.art === "tabelle") {
    return (
      <EditorGitterWahl
        anker={anfrage.anker}
        beiSchliessen={beiSchliessen}
        beiWahl={(zeilen, spalten) => {
          const ergebnis = befehle.tabelle(zeilen, spalten);
          if (ergebnis.ok) beiSchliessen();
        }}
      />
    );
  }
  if (anfrage.art === "seite" && sucheSeiten) {
    return <EditorSeitenWahl offen suche={sucheSeiten} beiVerlinken={befehle.seiteVerlinken} beiSchliessen={beiSchliessen} />;
  }
  return null;
}
